import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  Biometric,
  BiometricReferenceRange,
  BiometricResult,
  LabVisit,
  LabVisitRequisition,
  Question,
  QuestionAlternative,
  QuestionNumericRule,
  QuestionRespons,
} from "@/lib/api_client";
import {
  applyBioIdSubstitutions,
  applyHelfoRulesV2,
  buildFurstIdGroups,
  expandToFurstGroups,
  getDefaultBioIds,
  getDrDropinBhtPrivatePanelBioIds,
  getFixedPanelHelfoSubstitutions,
  getKickstartPanelBioIds,
  getLatestResultDates,
  getLpaConditionalBioId,
  getPreviouslyMeasuredBioIds,
  getSurveyBioIds,
  partitionOutsideRangeBioIds,
  resolveAppliedBioIdSubstitutions,
  resolveFixedPanelPrivatBioIds,
  type SkippedHistoricalEntry,
} from "@/lib/utils/bloodTestSuggestion";
import {
  useHelfoBiometricMap,
  useLabVisitHelfoEvaluation,
  useValidateClinicalInfo,
  labVisitHelfoEvaluationQueryKey,
  labVisitHelfoEvaluationQueryFn,
  type ClinicalInfoViolation,
  type DedupWarning,
  type EvaluateResult,
  type HelfoRulesVersion,
  type LabVisitEvaluateResult,
} from "@/hooks/api/useHelfoEngine";
import {
  useLabVisitRequisitionBiometrics,
  useLabVisitRequisitions,
  useSyncLabVisitMarkers,
} from "@/hooks/api/useLabVisits";

// The privat-rekvisisjon source toggles. HELFO is fully determined by
// `helfoLockedSet` (engine + currently-outside-range) and is not
// represented here.
export type PrivatSourceKey =
  | "defaults"
  | "survey"
  | "previousMarkers"
  | "previouslyOutsideRange"
  | "enginePrivat";

export type PrivatSourceEntry = {
  /** Marker IDs this source would contribute, before HELFO filtering. */
  source: Set<string>;
  /** Source minus any markers locked into HELFO. */
  effectiveSource: Set<string>;
  /** Whether every marker in effectiveSource is currently in non-HELFO. */
  selected: boolean;
  /** True when some (but not all) markers in effectiveSource are in non-HELFO. */
  partial: boolean;
};

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/**
 * Keep only the dedup warnings that still describe what is being sent. The
 * engine derives them from the panel it would have built, so a warning about a
 * marker the doctor has since dropped from the HELFO requisition — the usual
 * answer to "this was taken a month ago" — would otherwise keep nagging about
 * a duplicate that is no longer there.
 */
export function dedupWarningsForHelfoSet(
  warnings: DedupWarning[],
  slugToBioId: Map<string, string>,
  helfoSet: Set<string>,
): DedupWarning[] {
  return warnings.filter((w) =>
    w.covers.some((slug) => {
      const bioId = slugToBioId.get(slug);
      return bioId !== undefined && helfoSet.has(bioId);
    }),
  );
}

/**
 * The engine's HELFO markers (sti A) and the previously abnormal ones (sti B)
 * under the chosen rules, before any fixed-panel swap.
 */
function helfoPaths(params: {
  rules: HelfoRulesVersion;
  evaluation: LabVisitEvaluateResult | undefined;
  outside: Set<string>;
  slugToBioId: Map<string, string>;
  lastTaken: Map<string, Date>;
  furstIdGroups: Map<string, Set<string>>;
}): { engine: Set<string>; outside: Set<string> } {
  const { rules, evaluation, outside, slugToBioId } = params;
  const toIds = (slugs: string[]) =>
    new Set(slugs.flatMap((slug) => slugToBioId.get(slug) ?? []));
  const engine = toIds(evaluation?.helfo.biomarkers ?? []);
  if (rules === "v1") return { engine, outside };
  return applyHelfoRulesV2({
    engine,
    outside,
    blocked: toIds(Object.keys(evaluation?.knownConditionBlocks ?? {})),
    lastTaken: params.lastTaken,
    now: new Date(),
    furstIdGroups: params.furstIdGroups,
  });
}

export type LabVisitHelfoPartitioning = {
  engineEval: EvaluateResult | undefined;
  engineLoading: boolean;
  /** The rules the last initialise or re-run used. Not stored: a reload shows v1. */
  rulesVersion: HelfoRulesVersion;
  /** True once the requisition + biometric queries have loaded. */
  dbLoaded: boolean;
  helfoReq: LabVisitRequisition | undefined;
  nonHelfoReq: LabVisitRequisition | undefined;
  helfoBioIds: Set<string>;
  nonHelfoBioIds: Set<string>;
  /** The canonical HELFO set = engineHelfoSet ∪ currentlyOutsideRange. */
  helfoLockedSet: Set<string>;
  /**
   * The HELFO set actually in effect for display and sync:
   * - Normal mode: same as `helfoLockedSet` (engine-derived).
   * - Doctor-override mode: the manually managed override set.
   *
   * Either way it is widened to whole Fürst analysis codes, so it can contain
   * markers neither the engine nor the doctor named — a code is one
   * measurement and its markers can't be split across the two requisitions.
   */
  effectiveHelfoSet: Set<string>;
  engineHelfoSet: Set<string>;
  currentlyOutsideRange: Set<string>;
  /**
   * Fixed-panel HELFO swaps that fired on this visit (fasting → non-fasting
   * glucose on Dr.Dropin BHT and Kickstart). Empty on ordinary visits and
   * whenever the HELFO side never asked for a substituted marker.
   */
  appliedHelfoSubstitutions: {
    fromBiometricId: string;
    toBiometricId: string;
  }[];
  /** Historical out-of-range markers we wanted to follow up but can't send. */
  skippedHistorical: SkippedHistoricalEntry[];
  /** bioId → cluster display names, for HELFO marker tooltips. */
  helfoReasonNames: Map<string, string[]>;
  /** Engine slugs not mapped to an Amino biometric (catalog gap). */
  catalogGapSlugs: string[];
  /** Dedup warnings still describing markers on the HELFO requisition. */
  dedupWarnings: DedupWarning[];
  activeSources: Record<PrivatSourceKey, PrivatSourceEntry>;
  /**
   * Add or remove a marker from the privat partition. Markers sharing the
   * clicked marker's Fürst analysis code follow it in or out — one code is one
   * order, so they can't be picked apart.
   */
  togglePrivat: (bioId: string) => void;
  togglePrivatSource: (key: PrivatSourceKey) => void;
  /**
   * Re-runs the HELFO engine for this visit: re-fetches engine output (via
   * React Query invalidation) and overwrites the editable HELFO + non-HELFO
   * drafts with the engine's current proposal — markers, reasons, and
   * clinicalInfo all reset as if it were a first creation. Returns the
   * clinicalInfo string that was written so the caller can sync any local
   * editor state. Throws if any requisition on this visit is no longer
   * editable.
   */
  rerunEngine: (
    rules: HelfoRulesVersion,
  ) => Promise<{ clinicalInfo: string | null }>;
  /** True while a re-run mutation is in flight. */
  rerunPending: boolean;
  /** True if any requisition on this visit is past the editable phase. */
  hasNonEditableRequisition: boolean;
  /**
   * True for `drDropinBht` visits. The privat partition is anchored to the
   * fixed Dr.Dropin BHT panel — the source toggles don't apply and the panel's
   * own markers should be rendered disabled. HELFO partition follows the
   * standard rules regardless.
   */
  isDrDropinBhtPanel: boolean;
  /**
   * True for `kickstart` visits. Same deal as `isDrDropinBhtPanel`, with the
   * Kickstart panel as the fixed set. Kept as a separate flag rather than
   * folded into one because the two show different copy — the doctor needs to
   * know *which* panel they're looking at.
   */
  isKickstartPanel: boolean;
  /**
   * True when the privat partition is anchored to a fixed panel (BHT or
   * Kickstart) rather than doctor-toggleable sources. This is the flag to gate
   * the source toggles on; the two specific flags above are for wording only.
   */
  isFixedPanel: boolean;
  /**
   * The markers of this visit's fixed panel — empty for ordinary visits. The
   * panel is a floor, not a ceiling: these markers can't be removed from the
   * privat partition, but the doctor can add extra markers on top of them
   * (`togglePrivat` accepts anything outside this set). Callers use it to tell
   * panel markers from doctor-added extras.
   */
  fixedPanelBioIds: Set<string>;
  /**
   * A marker plus everything ordered together with it on the same Fürst
   * analysis code — just the marker itself when it is alone on its code. Lets
   * callers tell a marker that arrived with its code from one someone picked.
   */
  furstGroupOf: (bioId: string) => Set<string>;
  /** True when a doctor has manually unlocked the HELFO partition for editing. */
  isHelfoUnlocked: boolean;
  /**
   * Activates doctor-override mode, seeding the override set from the current
   * DB HELFO state. From this point, `effectiveHelfoSet` reflects manual edits
   * and the continuous-sync writes them to the DB.
   */
  unlockHelfo: () => void;
  /**
   * Closes the edit UI without clearing the override. The doctor's HELFO edits
   * remain committed to the DB. Only `rerunEngine()` reverts to engine output.
   */
  relockHelfo: () => void;
  /**
   * Add or remove a marker from the doctor-override HELFO set, along with the
   * rest of its Fürst analysis code. No-op when not unlocked.
   */
  toggleHelfo: (bioId: string) => void;
  /**
   * True when data has loaded, no requisitions exist yet, and the user has
   * not yet clicked "Initialize". Shows an initialization CTA in the UI
   * instead of running the engine automatically.
   */
  needsHelfoInit: boolean;
  /**
   * Triggers the initial engine run under `rules`, seeding both HELFO and
   * non-HELFO drafts. Only effective when `needsHelfoInit` is true.
   */
  initializeHelfo: (rules: HelfoRulesVersion) => void;
  /**
   * Removes all markers from the HELFO partition by activating an empty
   * doctor override. The override persists until the doctor re-runs the engine.
   */
  clearHelfo: () => void;
};

/**
 * Owns the HELFO/privat partition state for a lab visit's marker selection
 * step. Pulls engine output + DB requisitions, derives the locked HELFO
 * set, and continuously syncs the DB to match (creating drafts lazily,
 * stripping HELFO overlap from non-HELFO, seeding clinicalInfo on first
 * creation).
 *
 * Invariants enforced:
 *   1. HELFO partition == engineHelfoSet ∪ currentlyOutsideRange.
 *   2. non-HELFO ∩ HELFO = ∅.
 * Plus a one-shot seed of `defaults` into non-HELFO on first open.
 */
export function useLabVisitHelfoPartitioning(params: {
  visit: LabVisit;
  allBiometrics: Biometric[];
  bioResults: BiometricResult[];
  sortedVisits: LabVisit[];
  questions: Question[];
  questionAlternatives: QuestionAlternative[];
  questionNumericRules: QuestionNumericRule[];
  questionResponses: QuestionRespons[];
  referenceRanges: BiometricReferenceRange[];
  userGender?: string | null;
  userAge?: number | null;
}): LabVisitHelfoPartitioning {
  const {
    visit,
    allBiometrics,
    bioResults,
    sortedVisits,
    questions,
    questionAlternatives,
    questionNumericRules,
    questionResponses,
    referenceRanges,
    userGender,
    userAge,
  } = params;

  const qc = useQueryClient();
  const { data: selectedBiometrics = [], isSuccess: dbLoaded } =
    useLabVisitRequisitionBiometrics(visit.id);
  const { data: requisitions = [] } = useLabVisitRequisitions(visit.id);
  const [rulesVersion, setRulesVersion] = useState<HelfoRulesVersion>("v1");
  const { data: engineEval, isLoading: engineLoading } =
    useLabVisitHelfoEvaluation(visit.id, rulesVersion);
  const { data: biometricMap = [] } = useHelfoBiometricMap();
  const syncMarkers = useSyncLabVisitMarkers();

  // Editable requisitions only — submitted/cancelled rows are read-only.
  const editableReqs = useMemo(
    () =>
      requisitions.filter(
        (r) => r.status === "draft" || r.status === "readyForSubmission",
      ),
    [requisitions],
  );
  const helfoReq = editableReqs.find((r) => r.helfo);
  const nonHelfoReq = editableReqs.find((r) => !r.helfo);
  const hasNonEditableRequisition = requisitions.some(
    (r) => r.status !== "draft" && r.status !== "readyForSubmission",
  );

  // Partition by which editable requisition each marker row belongs to.
  // Markers attached to non-editable requisitions are excluded defensively.
  const { helfoBioIds, nonHelfoBioIds } = useMemo(() => {
    const helfo = new Set<string>();
    const nonHelfo = new Set<string>();
    for (const m of selectedBiometrics) {
      if (helfoReq && m.requisitionId === helfoReq.id) helfo.add(m.biometricId);
      else if (nonHelfoReq && m.requisitionId === nonHelfoReq.id)
        nonHelfo.add(m.biometricId);
    }
    return { helfoBioIds: helfo, nonHelfoBioIds: nonHelfo };
  }, [selectedBiometrics, helfoReq, nonHelfoReq]);

  const slugToBioId = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of biometricMap) {
      if (e.biometricId) m.set(e.slug, e.biometricId);
    }
    return m;
  }, [biometricMap]);

  const isDrDropinBhtPanel = visit.b2bFlowType === "drDropinBht";
  const isKickstartPanel = visit.b2bFlowType === "kickstart";
  const isFixedPanel = isDrDropinBhtPanel || isKickstartPanel;

  // Fixed-panel marker swaps for the HELFO side (currently fasting →
  // non-fasting glucose). Empty on ordinary visits, which makes every
  // `applyBioIdSubstitutions` call below a pass-through there.
  const helfoSubstitutions = useMemo(
    () =>
      isFixedPanel
        ? getFixedPanelHelfoSubstitutions(allBiometrics)
        : new Map<string, string>(),
    [isFixedPanel, allBiometrics],
  );

  const enginePrivatSet = useMemo(() => {
    const s = new Set<string>();
    for (const slug of engineEval?.comparison.only_in_privat ?? []) {
      const id = slugToBioId.get(slug);
      if (id) s.add(id);
    }
    return s;
  }, [engineEval, slugToBioId]);

  const catalogGapSlugs = useMemo(() => {
    if (!engineEval) return [];
    const all = new Set<string>([
      ...engineEval.helfo.biomarkers,
      ...engineEval.comparison.only_in_privat,
    ]);
    return [...all].filter((s) => !slugToBioId.has(s));
  }, [engineEval, slugToBioId]);

  const helfoReasonNames = useMemo(() => {
    const m = new Map<string, string[]>();
    if (!engineEval) return m;
    const clusterNameById = new Map(
      engineEval.helfo.cluster_details.map((c) => [
        c.cluster_id,
        c.cluster_name,
      ]),
    );
    for (const [slug, clusterIds] of Object.entries(
      engineEval.helfo.panel_provenance,
    )) {
      const bioId = slugToBioId.get(slug);
      if (!bioId) continue;
      // Provenance follows the swap, so a substituted marker still shows
      // the cluster that asked for it rather than reading as doctor-added.
      m.set(
        helfoSubstitutions.get(bioId) ?? bioId,
        clusterIds.map((id) => clusterNameById.get(id) ?? id),
      );
    }
    return m;
  }, [engineEval, slugToBioId, helfoSubstitutions]);

  // bioId → comma-joined cluster IDs (persisted as helfo_reason).
  const helfoReasonIds = useMemo(() => {
    const m: Record<string, string> = {};
    if (!engineEval) return m;
    for (const [slug, clusterIds] of Object.entries(
      engineEval.helfo.panel_provenance,
    )) {
      const bioId = slugToBioId.get(slug);
      if (!bioId) continue;
      m[helfoSubstitutions.get(bioId) ?? bioId] = clusterIds.join(",");
    }
    return m;
  }, [engineEval, slugToBioId, helfoSubstitutions]);

  const lpaConditionalBioId = useMemo(
    () =>
      getLpaConditionalBioId({
        biometrics: allBiometrics,
        bioResults,
        sortedVisits,
        currentVisitId: visit.id,
      }),
    [allBiometrics, bioResults, sortedVisits, visit.id],
  );
  // Markers sharing a Fürst analysis code move as a unit — see
  // `buildFurstIdGroups`.
  const furstIdGroups = useMemo(
    () => buildFurstIdGroups(allBiometrics),
    [allBiometrics],
  );

  const defaultBioIds = useMemo(() => {
    const ids = getDefaultBioIds(allBiometrics);
    if (lpaConditionalBioId) ids.add(lpaConditionalBioId);
    return ids;
  }, [allBiometrics, lpaConditionalBioId]);

  const drDropinBhtPanelBioIds = useMemo(
    () => getDrDropinBhtPrivatePanelBioIds(allBiometrics),
    [allBiometrics],
  );
  const kickstartPanelBioIds = useMemo(
    () => getKickstartPanelBioIds(allBiometrics),
    [allBiometrics],
  );
  // The panel this visit is anchored to (empty when it isn't a fixed-panel
  // flow), so panel handling reads the same everywhere instead of re-picking
  // between the two sets at each use site.
  // Expanded to whole Fürst codes like every other marker set: a panel marker's
  // siblings arrive with it and can't be ordered away, so they belong to the
  // floor too. Leaving them out would render them as ordinary privat additions
  // the doctor can untick, and the next sync would just put them back.
  const fixedPanelBioIds = useMemo(() => {
    if (isDrDropinBhtPanel)
      return expandToFurstGroups(drDropinBhtPanelBioIds, furstIdGroups);
    if (isKickstartPanel)
      return expandToFurstGroups(kickstartPanelBioIds, furstIdGroups);
    return new Set<string>();
  }, [
    isDrDropinBhtPanel,
    isKickstartPanel,
    drDropinBhtPanelBioIds,
    kickstartPanelBioIds,
    furstIdGroups,
  ]);
  const surveyBioIds = useMemo(
    () =>
      getSurveyBioIds({
        biometrics: allBiometrics,
        questions,
        questionAlternatives,
        questionNumericRules,
        questionResponses,
      }),
    [
      allBiometrics,
      questions,
      questionAlternatives,
      questionNumericRules,
      questionResponses,
    ],
  );
  // Only biometrics with a furstId can be requisitioned to Fürst. Filtering
  // here keeps retired-catalog markers from previous visits out of the HELFO
  // partition — otherwise the Fürst send drops them silently and can crash
  // on an empty-after-filter requisition.
  const sendableBiometricIds = useMemo(() => {
    const s = new Set<string>();
    for (const b of allBiometrics) {
      if (b.furstId) s.add(b.id);
    }
    return s;
  }, [allBiometrics]);

  /** The clicked marker plus every marker ordered together with it. */
  const furstGroupOf = useCallback(
    (bioId: string): Set<string> =>
      furstIdGroups.get(bioId) ?? new Set([bioId]),
    [furstIdGroups],
  );

  const {
    currentlyOutside: currentlyOutsideAll,
    previouslyOutside: previouslyOutsideRange,
    skippedHistorical,
  } = useMemo(
    () =>
      partitionOutsideRangeBioIds({
        currentVisitId: visit.id,
        sortedVisits,
        bioResults,
        referenceRanges,
        gender: userGender,
        age: userAge,
        sendableBiometricIds,
      }),
    [
      visit.id,
      sortedVisits,
      bioResults,
      referenceRanges,
      userGender,
      userAge,
      sendableBiometricIds,
    ],
  );

  const lastTaken = useMemo(
    () =>
      getLatestResultDates({
        currentVisitId: visit.id,
        sortedVisits,
        bioResults,
      }),
    [visit.id, sortedVisits, bioResults],
  );

  const { engine: engineHelfoSetRaw, outside: currentlyOutsideRaw } = useMemo(
    () =>
      helfoPaths({
        rules: rulesVersion,
        evaluation: engineEval,
        outside: currentlyOutsideAll,
        slugToBioId,
        lastTaken,
        furstIdGroups,
      }),
    [
      rulesVersion,
      engineEval,
      currentlyOutsideAll,
      slugToBioId,
      lastTaken,
      furstIdGroups,
    ],
  );

  const engineHelfoSet = useMemo(
    () => applyBioIdSubstitutions(engineHelfoSetRaw, helfoSubstitutions),
    [engineHelfoSetRaw, helfoSubstitutions],
  );

  // Currently-outside-range markers land in HELFO, so they get the same
  // fixed-panel swap as the engine's own suggestions — following up an
  // abnormal fasting glucose with a fasting re-draw is exactly what
  // these flows can't ask of the patient.
  const currentlyOutsideRange = useMemo(
    () => applyBioIdSubstitutions(currentlyOutsideRaw, helfoSubstitutions),
    [currentlyOutsideRaw, helfoSubstitutions],
  );

  // Everything the patient has been tested for before (self-reported visits
  // excluded). Opt-in only — never part of the first-open seed, since "all
  // markers ever taken" is a re-order shortcut, not a clinical default.
  const previouslyMeasuredBioIds = useMemo(
    () =>
      getPreviouslyMeasuredBioIds({
        currentVisitId: visit.id,
        sortedVisits,
        bioResults,
        sendableBiometricIds,
      }),
    [visit.id, sortedVisits, bioResults, sendableBiometricIds],
  );

  const helfoLockedSet = useMemo(() => {
    const s = new Set<string>();
    for (const id of engineHelfoSet) s.add(id);
    for (const id of currentlyOutsideRange) s.add(id);
    return s;
  }, [engineHelfoSet, currentlyOutsideRange]);

  // Doctor-override mode.
  // `helfoOverride` — when non-null, is the authoritative HELFO set for both
  //   display and DB sync. Stays set even after the doctor re-locks the UI,
  //   so the doctor's edits are preserved. Only cleared by rerunEngine().
  // `isHelfoUnlocked` — UI edit flag: true = checkboxes interactive.
  //   Flipping it to false ("re-lock") does NOT clear the override.
  const [helfoOverride, setHelfoOverride] = useState<Set<string> | null>(null);
  const [isHelfoUnlocked, setIsHelfoUnlocked] = useState(false);
  const [isHelfoInitialized, setIsHelfoInitialized] = useState(false);

  // The HELFO set that drives both display and sync:
  //   - Override active: doctor's manually managed set.
  //   - Either draft exists: always reflects DB (helfoBioIds) — never
  //     re-derives from engine output so doctor's clears/edits survive page
  //     reloads. A privat draft alone means HELFO was saved empty: emptying
  //     deletes the HELFO draft, and the rules version a v2 initialise ran
  //     under is not stored, so the engine would re-fill it with v1 markers.
  //   - No draft yet: show engine preview so the doctor sees what will be
  //     created when they click Initialize.
  // A Fürst analysis code is one measurement. Whichever markers behind it the
  // engine or the doctor picked, the code goes on the HELFO requisition and
  // every marker behind it comes back with it — so the whole group belongs on
  // the HELFO side. Expanding here is also what keeps siblings off the privat
  // side: nothing dedupes across the two requisitions, so a sibling left in
  // privat would order, and bill, a code HELFO is already paying for.
  const effectiveHelfoSet = useMemo(
    () =>
      expandToFurstGroups(
        helfoOverride !== null
          ? helfoOverride
          : helfoReq || nonHelfoReq
            ? helfoBioIds
            : helfoLockedSet,
        furstIdGroups,
      ),
    [
      helfoOverride,
      helfoReq,
      nonHelfoReq,
      helfoBioIds,
      helfoLockedSet,
      furstIdGroups,
    ],
  );

  const dedupWarnings = useMemo(
    () =>
      dedupWarningsForHelfoSet(
        engineEval?.helfo.dedup_warnings ?? [],
        slugToBioId,
        effectiveHelfoSet,
      ),
    [engineEval, slugToBioId, effectiveHelfoSet],
  );

  // The swaps that actually fired, so the HELFO card can say why the
  // requisition doesn't match the NOKLUS panel verbatim. Gated on
  // `effectiveHelfoSet`, not just on what the engine asked for: an existing
  // draft is authoritative over engine output, so a visit whose requisition
  // predates the rule still carries the original marker and has no swap to
  // explain.
  const appliedHelfoSubstitutions = useMemo(() => {
    const requested = new Set<string>([
      ...engineHelfoSetRaw,
      ...currentlyOutsideRaw,
    ]);
    return resolveAppliedBioIdSubstitutions({
      substitutions: helfoSubstitutions,
      requested,
      effectiveHelfo: effectiveHelfoSet,
    });
  }, [
    helfoSubstitutions,
    engineHelfoSetRaw,
    currentlyOutsideRaw,
    effectiveHelfoSet,
  ]);

  // Privat-only source toggles, each restricted to markers NOT in the
  // effective HELFO set (engine-derived or doctor-override).
  const activeSources: Record<PrivatSourceKey, PrivatSourceEntry> =
    useMemo(() => {
      const restrict = (full: Set<string>): Set<string> => {
        const s = new Set<string>();
        for (const id of full) if (!effectiveHelfoSet.has(id)) s.add(id);
        return s;
      };
      const sources: Record<PrivatSourceKey, { source: Set<string> }> = {
        defaults: { source: defaultBioIds },
        survey: { source: surveyBioIds },
        previousMarkers: { source: previouslyMeasuredBioIds },
        previouslyOutsideRange: { source: previouslyOutsideRange },
        enginePrivat: { source: enginePrivatSet },
      };
      const result = {} as Record<PrivatSourceKey, PrivatSourceEntry>;
      for (const [k, v] of Object.entries(sources) as Array<
        [PrivatSourceKey, { source: Set<string> }]
      >) {
        const effective = restrict(v.source);
        const allSelected =
          effective.size > 0 && effective.isSubsetOf(nonHelfoBioIds);
        result[k] = {
          source: v.source,
          effectiveSource: effective,
          selected: allSelected,
          partial:
            !allSelected &&
            effective.size > 0 &&
            [...effective].some((id) => nonHelfoBioIds.has(id)),
        };
      }
      return result;
    }, [
      defaultBioIds,
      surveyBioIds,
      previouslyMeasuredBioIds,
      previouslyOutsideRange,
      enginePrivatSet,
      effectiveHelfoSet,
      nonHelfoBioIds,
    ]);

  // Auto-mark initialized when requisitions already exist on load so the
  // continuous-sync effect runs normally for returning visits.
  useEffect(() => {
    if (dbLoaded && (helfoReq || nonHelfoReq)) setIsHelfoInitialized(true);
  }, [dbLoaded, helfoReq, nonHelfoReq]);

  // Continuous-sync effect. See hook doc comment for invariants.
  // helfoReq / nonHelfoReq are already filtered to draft|readyForSubmission
  // by `editableReqs` above — no need to re-check status here. Once a
  // requisition leaves the editable set (submitted+), the FE simply won't
  // see it via these references, and this effect leaves the DB untouched.
  //
  // Visit-status guard: don't auto-create reqs on visits that haven't
  // entered the requisition flow. The backend route refuses these with
  // 409; this short-circuits before the request to avoid noisy errors
  // when CRM opens a paid patient's anamnesis.
  useEffect(() => {
    if (!dbLoaded || engineLoading || biometricMap.length === 0) return;
    if (visit.status === "paid" || visit.status === "requestRejected") return;
    // Block first-time creation until the user explicitly clicks Initialize.
    if (!isHelfoInitialized && !helfoReq && !nonHelfoReq) return;

    // effectiveHelfoSet already encodes the correct HELFO intent
    // (override → override set, existing req → DB state, new req → engine).
    const wantedHelfo = effectiveHelfoSet;

    // Non-HELFO seed:
    //   Fixed-panel flows (drDropinBht, kickstart): always contain that flow's
    //   panel (minus HELFO overlap). Re-applied continuously so the panel
    //   can't accidentally drift, and HELFO growth automatically removes
    //   overlapping markers. Markers the doctor added on top of the panel are
    //   carried over — the panel is a floor, not a ceiling.
    //
    //   Otherwise: on first open (no draft yet) seed with defaults + survey +
    //   engine-suggested privat (all minus HELFO overlap).
    //   `previouslyOutsideRange` and `previousMarkers` stay opt-in — both are
    //   stable historical results, not signals the doctor needs by default.
    //   Afterwards just strip any HELFO overlap that crept in
    //   (e.g. engine output expanded into a marker the doctor previously had
    //   in privat).
    let seededNonHelfo = new Set<string>();
    if (isFixedPanel) {
      seededNonHelfo = resolveFixedPanelPrivatBioIds({
        panel: fixedPanelBioIds,
        currentPrivat: nonHelfoBioIds,
        helfo: wantedHelfo,
      });
    } else if (!nonHelfoReq) {
      for (const id of defaultBioIds) seededNonHelfo.add(id);
      for (const id of surveyBioIds) seededNonHelfo.add(id);
      for (const id of enginePrivatSet) seededNonHelfo.add(id);
    } else {
      seededNonHelfo = nonHelfoBioIds;
    }

    // Whole codes here too: defaults, survey answers and engine suggestions all
    // name single markers, but the code behind one returns its siblings for the
    // same charge, so the partition shows what the patient actually gets.
    // Expanding before the HELFO strip is what keeps the two requisitions
    // disjoint — `wantedHelfo` is expanded as well, so a code that went to
    // HELFO is taken out of privat in full rather than leaving a sibling behind.
    const wantedNonHelfo = new Set(
      [...expandToFurstGroups(seededNonHelfo, furstIdGroups)].filter(
        (id) => !wantedHelfo.has(id),
      ),
    );

    const helfoDiffers = !setsEqual(helfoBioIds, wantedHelfo);
    const nonHelfoDiffers = !setsEqual(nonHelfoBioIds, wantedNonHelfo);
    if (!helfoDiffers && !nonHelfoDiffers) return;

    const reasons: Record<string, string> = {};
    for (const id of wantedHelfo) {
      if (helfoReasonIds[id]) reasons[id] = helfoReasonIds[id];
      else if (currentlyOutsideRange.has(id))
        reasons[id] = "outside_range:recent_abnormal";
    }
    const isFirstHelfoCreation = !helfoReq && wantedHelfo.size > 0;

    syncMarkers.mutate({
      labVisitId: visit.id,
      helfoBiometricIds: helfoDiffers ? [...wantedHelfo] : undefined,
      nonHelfoBiometricIds: nonHelfoDiffers ? [...wantedNonHelfo] : undefined,
      clinicalInfo: isFirstHelfoCreation
        ? (() => {
            if (!engineEval) return null;
            const hasOOR = currentlyOutsideRange.size > 0;
            const hasClusters = engineEval.helfo.active_clusters.length > 0;
            if (!hasClusters && hasOOR)
              return "Kontroll av tidligere unormale prøvesvar.";
            if (hasClusters && hasOOR)
              return `${engineEval.helfo.requisition_text} Inkluderer også tidligere unormale prøvesvar.`;
            return engineEval.helfo.requisition_text ?? null;
          })()
        : undefined,
      helfoReasons: Object.keys(reasons).length > 0 ? reasons : undefined,
    });
  }, [
    dbLoaded,
    engineLoading,
    biometricMap.length,
    helfoReq,
    nonHelfoReq,
    helfoBioIds,
    nonHelfoBioIds,
    helfoLockedSet,
    effectiveHelfoSet,
    defaultBioIds,
    surveyBioIds,
    enginePrivatSet,
    fixedPanelBioIds,
    isFixedPanel,
    currentlyOutsideRange,
    engineEval,
    helfoReasonIds,
    // helfoOverride: when the doctor toggles a HELFO marker, the override set
    // changes reference → effect re-runs → syncs the new wanted set to DB.
    helfoOverride,
    // `syncMarkers.mutate` is the stable function reference; depending on
    // the whole mutation object would re-run this effect every parent
    // render because React Query returns a new object identity each time.
    syncMarkers.mutate,
    visit.id,
    visit.status,
    isHelfoInitialized,
  ]);

  // Action: persist a new non-HELFO set, defensively stripping any
  // overlap with the effective HELFO set before the PUT.
  const persistNonHelfo = (next: Set<string>) => {
    const clean = new Set<string>();
    for (const id of next) if (!effectiveHelfoSet.has(id)) clean.add(id);
    syncMarkers.mutate({
      labVisitId: visit.id,
      nonHelfoBiometricIds: [...clean],
    });
  };

  const togglePrivat = (bioId: string) => {
    // Both guards cover the clicked marker's whole analysis code, because both
    // sets hold whole codes: a sibling in HELFO means every sibling is, and a
    // sibling on a fixed panel means the code is part of that panel's floor.
    // So the group below can never be split by either of them.
    if (effectiveHelfoSet.has(bioId)) return;
    // Fixed-panel flows: the panel itself is not negotiable, but markers
    // outside it are ordinary privat additions the doctor may add or drop.
    // `fixedPanelBioIds` is empty for other visits, so this is a no-op there.
    if (fixedPanelBioIds.has(bioId)) return;
    const next = new Set(nonHelfoBioIds);
    const adding = !next.has(bioId);
    for (const id of furstGroupOf(bioId)) {
      if (adding) next.add(id);
      else next.delete(id);
    }
    persistNonHelfo(next);
  };

  const togglePrivatSource = (key: PrivatSourceKey) => {
    if (isFixedPanel) return;
    const src = activeSources[key];
    const next = new Set(nonHelfoBioIds);
    // Whole codes, both directions. A source names single markers while privat
    // holds their whole analysis code, so deleting only what the source names
    // strands the siblings — and the next sync expands those straight back into
    // the markers the doctor just switched off, leaving the source unturnable.
    const group = expandToFurstGroups(src.effectiveSource, furstIdGroups);
    if (src.selected) for (const id of group) next.delete(id);
    else for (const id of group) next.add(id);
    persistNonHelfo(next);
  };

  const unlockHelfo = useCallback(() => {
    // Seed the override from the current DB HELFO state so the doctor
    // starts from what's already committed, then can freely edit.
    setHelfoOverride(new Set(helfoBioIds));
    setIsHelfoUnlocked(true);
  }, [helfoBioIds]);

  const relockHelfo = useCallback(() => {
    // Close the edit UI only — the override set stays active so the
    // continuous-sync keeps the doctor's edits in the HELFO requisition.
    // Only rerunEngine() reverts fully to engine output.
    setIsHelfoUnlocked(false);
  }, []);

  const toggleHelfo = useCallback(
    (bioId: string) => {
      // Direction comes from the effective set rather than the override it is
      // derived from: siblings pulled in by the Fürst expansion render as
      // checked without being stored, so reading the override would turn a
      // click meant to clear the group into one that re-adds it.
      const adding = !effectiveHelfoSet.has(bioId);
      const group = furstGroupOf(bioId);
      setHelfoOverride((prev) => {
        if (!prev) return prev; // no-op when not in override mode
        const next = new Set(prev);
        for (const id of group) {
          if (adding) next.add(id);
          else next.delete(id);
        }
        return next;
      });
    },
    [effectiveHelfoSet, furstGroupOf],
  );

  // Re-run: refetch engine output and overwrite the editable drafts as if
  // we were creating them for the first time. Mirrors the seed branches of
  // the continuous-sync effect but is one-shot and explicit, so it always
  // re-applies defaults/survey/enginePrivat to non-HELFO (not "preserve
  // current") and always re-seeds the HELFO clinical info.
  //
  // We also invalidate the parent's upstream queries (lab visits, bio
  // results, question responses) so the next render's partition inputs
  // are fresh — necessary when the user clicks rerun after importing new
  // prior labs or recording new survey responses. The current call still
  // uses the captured closure values; any drift gets corrected by the
  // continuous-sync effect on the next render.
  //
  // `syncMarkers.mutateAsync` is pulled to a local before useCallback so
  // the dep array tracks the stable function reference rather than the
  // whole mutation object (whose identity changes every render).
  const syncMarkersMutateAsync = syncMarkers.mutateAsync;
  const rerunEngine = useCallback(
    async (rules: HelfoRulesVersion) => {
      if (hasNonEditableRequisition) {
        throw new Error(
          "Kan ikke kjøre motoren på nytt: lab visit har innsendte rekvisisjoner.",
        );
      }
      // Invalidate upstream inputs in parallel, and fetch the engine result
      // directly via fetchQuery (staleTime:0 forces a fresh request).
      // fetchQuery throws on network/server error so the caller can toast it.
      const [fresh] = await Promise.all([
        qc.fetchQuery<LabVisitEvaluateResult>({
          queryKey: labVisitHelfoEvaluationQueryKey(visit.id, rules),
          queryFn: labVisitHelfoEvaluationQueryFn(visit.id, rules),
          staleTime: 0,
        }),
        qc.invalidateQueries({ queryKey: ["lab-visits"] }),
        qc.invalidateQueries({ queryKey: ["impersonated-biometric-results"] }),
        qc.invalidateQueries({ queryKey: ["impersonated-question-responses"] }),
      ]);

      const paths = helfoPaths({
        rules,
        evaluation: fresh,
        outside: currentlyOutsideAll,
        slugToBioId,
        lastTaken,
        furstIdGroups,
      });
      const substitutedEngineHelfoSet = applyBioIdSubstitutions(
        paths.engine,
        helfoSubstitutions,
      );
      const outside = applyBioIdSubstitutions(
        paths.outside,
        helfoSubstitutions,
      );
      const freshEnginePrivatSet = new Set<string>();
      for (const slug of fresh.comparison.only_in_privat) {
        const id = slugToBioId.get(slug);
        if (id) freshEnginePrivatSet.add(id);
      }

      const wantedHelfo = new Set<string>();
      for (const id of substitutedEngineHelfoSet) wantedHelfo.add(id);
      for (const id of outside) wantedHelfo.add(id);

      let wantedNonHelfo = new Set<string>();
      if (isFixedPanel) {
        // Reset semantics: back to the bare panel, dropping any extras the
        // doctor had added — same as the non-fixed branch dropping manual adds.
        wantedNonHelfo = resolveFixedPanelPrivatBioIds({
          panel: fixedPanelBioIds,
          currentPrivat: new Set(),
          helfo: wantedHelfo,
        });
      } else {
        for (const id of defaultBioIds) {
          if (!wantedHelfo.has(id)) wantedNonHelfo.add(id);
        }
        for (const id of surveyBioIds) {
          if (!wantedHelfo.has(id)) wantedNonHelfo.add(id);
        }
        for (const id of freshEnginePrivatSet) {
          if (!wantedHelfo.has(id)) wantedNonHelfo.add(id);
        }
      }

      const freshReasonIds: Record<string, string> = {};
      for (const [slug, clusterIds] of Object.entries(
        fresh.helfo.panel_provenance,
      )) {
        const bioId = slugToBioId.get(slug);
        if (!bioId) continue;
        freshReasonIds[helfoSubstitutions.get(bioId) ?? bioId] =
          clusterIds.join(",");
      }
      const reasons: Record<string, string> = {};
      for (const id of wantedHelfo) {
        if (freshReasonIds[id]) reasons[id] = freshReasonIds[id];
        else if (outside.has(id)) reasons[id] = "outside_range:recent_abnormal";
      }

      const hasOOR = outside.size > 0;
      const hasClusters = fresh.helfo.active_clusters.length > 0;
      // Clinical info describes a HELFO requisition, so with nothing on the
      // HELFO side there is nothing to describe. Writing the engine's "Ingen
      // NOKLUS-cluster utløst…" fallback anyway is how a patient with no HELFO
      // indication ended up with a HELFO requisition holding zero markers —
      // the backend used to create the row off that text alone.
      const clinicalInfo =
        wantedHelfo.size === 0
          ? null
          : !hasClusters && hasOOR
            ? "Kontroll av tidligere unormale prøvesvar."
            : hasClusters && hasOOR
              ? `${fresh.helfo.requisition_text} Inkluderer også tidligere unormale prøvesvar.`
              : (fresh.helfo.requisition_text ?? null);

      await syncMarkersMutateAsync({
        labVisitId: visit.id,
        helfoBiometricIds: [...wantedHelfo],
        nonHelfoBiometricIds: [...wantedNonHelfo],
        clinicalInfo,
        helfoReasons: Object.keys(reasons).length > 0 ? reasons : undefined,
      });
      // Clear the doctor override so the engine is authoritative again.
      setHelfoOverride(null);
      setIsHelfoUnlocked(false);
      setRulesVersion(rules);
      return { clinicalInfo };
    },
    [
      hasNonEditableRequisition,
      visit.id,
      qc,
      slugToBioId,
      currentlyOutsideAll,
      lastTaken,
      furstIdGroups,
      defaultBioIds,
      surveyBioIds,
      fixedPanelBioIds,
      isFixedPanel,
      helfoSubstitutions,
      syncMarkersMutateAsync,
    ],
  );

  const initializeHelfo = useCallback((rules: HelfoRulesVersion) => {
    setRulesVersion(rules);
    setIsHelfoInitialized(true);
  }, []);

  const clearHelfo = useCallback(() => {
    setHelfoOverride(new Set());
  }, []);

  return {
    engineEval,
    engineLoading,
    rulesVersion,
    dbLoaded,
    helfoReq,
    nonHelfoReq,
    helfoBioIds,
    nonHelfoBioIds,
    helfoLockedSet,
    effectiveHelfoSet,
    engineHelfoSet,
    currentlyOutsideRange,
    appliedHelfoSubstitutions,
    skippedHistorical,
    helfoReasonNames,
    catalogGapSlugs,
    dedupWarnings,
    activeSources,
    furstGroupOf,
    togglePrivat,
    togglePrivatSource,
    rerunEngine,
    rerunPending: syncMarkers.isPending,
    hasNonEditableRequisition,
    isDrDropinBhtPanel,
    isKickstartPanel,
    isFixedPanel,
    fixedPanelBioIds,
    isHelfoUnlocked,
    unlockHelfo,
    relockHelfo,
    toggleHelfo,
    needsHelfoInit:
      dbLoaded &&
      !engineLoading &&
      biometricMap.length > 0 &&
      !isHelfoInitialized &&
      !helfoReq &&
      !nonHelfoReq,
    initializeHelfo,
    clearHelfo,
  };
}

// ---------------------------------------------------------------------------

export type HelfoClinicalInfoEditor = {
  clinicalInfo: string;
  setClinicalInfo: (text: string) => void;
  /** Call from `onBlur` of the textarea to persist. */
  persist: () => void;
  liveViolations: ClinicalInfoViolation[];
  /** True when the current text differs from what the engine would generate today. */
  differsFromEngine: boolean;
  /** Overwrite the current text with the engine's current output and persist. */
  resetToEngine: () => void;
};

/**
 * Edit buffer + debounced live-validation for the HELFO requisition's
 * `clinicalInfo` field. Initial value comes from the existing HELFO
 * requisition (if any) or the engine's auto-generated requisition_text.
 * Persists on blur via the markers endpoint (clinicalInfo only attaches
 * to the HELFO draft on the backend side).
 */
export function useHelfoClinicalInfoEditor(params: {
  labVisitId: string;
  helfoReq: LabVisitRequisition | undefined;
  engineRequisitionText: string | undefined;
  dbLoaded: boolean;
  engineLoading: boolean;
}): HelfoClinicalInfoEditor {
  const {
    labVisitId,
    helfoReq,
    engineRequisitionText,
    dbLoaded,
    engineLoading,
  } = params;

  const syncMarkers = useSyncLabVisitMarkers();
  const validate = useValidateClinicalInfo();
  const [clinicalInfo, setClinicalInfo] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [seededForReqId, setSeededForReqId] = useState<string | null>(null);
  const [liveViolations, setLiveViolations] = useState<ClinicalInfoViolation[]>(
    [],
  );

  // Seed once both DB and engine have loaded. Prefer existing draft text
  // over the engine's auto-generated value so doctor edits aren't
  // clobbered on re-mount.
  //
  // Keyed on which requisition the buffer was seeded from, not a plain
  // "have we seeded yet" flag: emptying the HELFO partition deletes the
  // draft, and filling it again creates a *different* row with freshly
  // generated clinical info. Same row → never re-seed, so edits survive
  // every re-render and re-mount; new row → the buffer follows it rather
  // than describing a requisition that no longer exists.
  const helfoReqId = helfoReq?.id ?? null;
  useEffect(() => {
    if (initialized && helfoReqId === seededForReqId) return;
    if (!dbLoaded) return;
    if (engineLoading) return;
    if (helfoReq && helfoReq.clinicalInfo) {
      setClinicalInfo(helfoReq.clinicalInfo);
    } else if (engineRequisitionText) {
      setClinicalInfo(engineRequisitionText);
    }
    setSeededForReqId(helfoReqId);
    setInitialized(true);
  }, [
    dbLoaded,
    engineLoading,
    helfoReq,
    helfoReqId,
    engineRequisitionText,
    initialized,
    seededForReqId,
  ]);

  // Debounced live validation. Best-effort: failures silently leave the
  // last violation list in place so a flaky network doesn't blank
  // warnings while the doctor is mid-edit.
  // Pull the stable mutateAsync reference out so we can put it in the
  // dep array honestly rather than disabling the lint.
  const validateMutateAsync = validate.mutateAsync;
  useEffect(() => {
    if (!initialized) return;
    if (!clinicalInfo.trim()) {
      setLiveViolations([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const violations = await validateMutateAsync(clinicalInfo);
        setLiveViolations(violations);
      } catch {
        // swallow
      }
    }, 300);
    return () => clearTimeout(id);
  }, [clinicalInfo, initialized, validateMutateAsync]);

  const persist = () =>
    syncMarkers.mutate({
      labVisitId,
      clinicalInfo: clinicalInfo || null,
    });

  const differsFromEngine =
    initialized &&
    !!engineRequisitionText &&
    clinicalInfo.trim() !== engineRequisitionText.trim();

  const resetToEngine = () => {
    if (!engineRequisitionText) return;
    setClinicalInfo(engineRequisitionText);
    syncMarkers.mutate({ labVisitId, clinicalInfo: engineRequisitionText });
  };

  return {
    clinicalInfo,
    setClinicalInfo,
    persist,
    liveViolations,
    differsFromEngine,
    resetToEngine,
  };
}
